import { useState } from "react";
import type { AnnouncementDetail } from "../../../hooks/useAdminAnnouncements";
import { retryableCount, sendableCount } from "../../../lib/announcements";
import own from "./Announcements.module.css";

export type ActionName = "send" | "retry" | "cancel";

type Props = {
  detail: AnnouncementDetail;
  busy: boolean;
  onAction: (action: ActionName) => void;
};

const plural = (n: number): string => `${n} instance${n === 1 ? "" : "s"}`;

// Sending is two clicks on purpose: the first shows the count it will go to.
export const AnnouncementActions = ({ detail, busy, onAction }: Props): JSX.Element => {
  const [confirming, setConfirming] = useState<boolean>(false);
  const { state } = detail.announcement;
  const sendable = sendableCount(detail.deliveries);
  const retryable = retryableCount(detail.deliveries);
  const checking = detail.deliveries.some((row) => row.previewStatus === "pending");

  return (
    <div className={own.actions}>
      {state === "draft" && !confirming && (
        <button
          type="button"
          className={own.primaryButton}
          disabled={busy || sendable === 0}
          onClick={() => setConfirming(true)}
          data-testid="announcement-send"
        >
          Send to {plural(sendable)}
        </button>
      )}
      {state === "draft" && confirming && (
        <>
          <button
            type="button"
            className={own.dangerButton}
            disabled={busy}
            onClick={() => onAction("send")}
            data-testid="announcement-send-confirm"
          >
            {busy ? "Working..." : `Yes, send to ${plural(sendable)} now`}
          </button>
          <button type="button" className={own.secondaryButton} onClick={() => setConfirming(false)}>
            Not yet
          </button>
          {checking && <span className={own.hint}>Some instances are still being checked.</span>}
        </>
      )}
      {(state === "sending" || state === "sent") && retryable > 0 && (
        <button
          type="button"
          className={own.primaryButton}
          disabled={busy}
          onClick={() => onAction("retry")}
          data-testid="announcement-retry"
        >
          Retry {plural(retryable)} that did not arrive
        </button>
      )}
      {(state === "draft" || state === "sending") && (
        <button
          type="button"
          className={own.secondaryButton}
          disabled={busy}
          onClick={() => onAction("cancel")}
          data-testid="announcement-cancel"
        >
          {state === "draft" ? "Discard" : "Stop sending"}
        </button>
      )}
    </div>
  );
};
